import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUser, faList } from '@fortawesome/free-solid-svg-icons';
import { fetchAdmins, deleteAdmin } from '../../../store/slices/adminSlice';
import adminService from '../../../services/adminService';
import { toast } from 'react-toastify';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import { formatDisplayError } from '../../../utils/passwordValidation';
import './AdminManagement.css';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';
import '../SystemCaregiverManagement/SystemCaregiverManagement.css';
import { buildDetailAddressLines, resolveStateDisplay } from '../../../utils/addressDisplay';
import { normalizeAdminStatus } from '../../../utils/adminStatus';
import { appendFromDashboardQuery } from '../../../utils/practiceNavigation';
import { canCreateAdmin, canEditAdmin, canDeleteAdmin } from '../../../utils/roleUtils';
import AddAdminModal from './AddAdmin/AddAdminModal';
import { STAFF_LIST_TABLE_CARD, staffCol } from '../PracticeDashboard/staffListTableLayout';
import { matchesWordStartAny } from '../../../utils/searchMatch';
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import EventLogSection from '../../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../../hooks/useEventLogModalScrollLock';

const AdminManagement = ({ practiceId, isReadOnly = false, onOpenEventLogs = null, hideEventLogsButton = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { clearBreadcrumbs, updateBreadcrumbs } = useBreadcrumb();
  const {
    admins,
    adminsByPracticeId,
    practiceAdminFetched,
    globalAdminsFetched,
    isLoading,
    error
  } = useSelector((state) => state.admin);
  const { user } = useSelector((state) => state.auth);
  const allowCreate = !isReadOnly && canCreateAdmin(user) && (practiceId || user?.role_id === 1);
  const allowEdit = !isReadOnly && canEditAdmin(user);
  const allowDelete = !isReadOnly && canDeleteAdmin(user);

  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [loadingAllAdmins, setLoadingAllAdmins] = useState(false);
  const [sortBy, setSortBy] = useState('status');
  const [sortOrder, setSortOrder] = useState('asc');
  const [optimisticDeletedIds, setOptimisticDeletedIds] = useState([]);
  
  // Modal state
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [adminToEdit, setAdminToEditModal] = useState(null);
  const [isViewAdminModal, setIsViewAdminModal] = useState(false);
  const [showLogSection, setShowLogSection] = useState(false);
  const logOverlayRef = React.useRef(null);
  const logContainerRef = React.useRef(null);
  useEventLogModalScrollLock(showLogSection, logOverlayRef, logContainerRef);

  const pidKey = practiceId != null ? String(practiceId) : null;
  const hasPracticeCache = pidKey && practiceAdminFetched[pidKey];
  const practiceAdminsList = pidKey ? (adminsByPracticeId[pidKey] ?? []) : null;
  const adminsSource =
    practiceId != null
      ? hasPracticeCache
        ? practiceAdminsList
        : null
      : admins;

  useEffect(() => {
    if (!practiceId) {
      updateBreadcrumbs([{ label: 'System Admins', path: '/admin', active: true }]);
      return () => clearBreadcrumbs();
    }
  }, [updateBreadcrumbs, clearBreadcrumbs, practiceId]);

  useEffect(() => {
    if (practiceId) {
      if (!practiceAdminFetched[String(practiceId)]) {
        dispatch(fetchAdmins({ practiceId }));
      }
    } else {
      if (!globalAdminsFetched) {
        fetchAllAdmins();
      }
    }
  }, [practiceId, dispatch, practiceAdminFetched, globalAdminsFetched]);

  const fetchAllAdmins = async () => {
    try {
      setLoadingAllAdmins(true);
      await dispatch(fetchAdmins({ practiceId: null, forceRefresh: true })).unwrap();
    } catch (e) {
      console.error('Error fetching all admins:', e);
    } finally {
      setLoadingAllAdmins(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [localSearchTerm, searchTerm]);

  useEffect(() => {
    if (!adminsSource) {
      setFilteredAdmins([]);
      return;
    }
    let filtered = adminsSource.filter((admin) => !admin.deleted_at);
    if (localSearchTerm.trim()) {
      filtered = filtered.filter((admin) => {
        const fullName = `${admin.first_name || ''} ${admin.last_name || ''}`;
        return matchesWordStartAny([fullName, admin.email, admin.phone], localSearchTerm);
      });
    }
    
    if (statusFilter !== 'All') {
      filtered = filtered.filter(admin => normalizeAdminStatus(admin.status) === statusFilter);
    }
    
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'idx') {
        const ai = Number(a.id) || 0;
        const bi = Number(b.id) || 0;
        return sortOrder === 'asc' ? ai - bi : bi - ai;
      }
      if (sortBy === 'status') {
        const o = { active: 1, inactive: 2 };
        const sa = (a.status || 'Active').toLowerCase();
        const sb = (b.status || 'Active').toLowerCase();
        const orderA = o[sa] || 3;
        const orderB = o[sb] || 3;
        if (orderA !== orderB) {
          return sortOrder === 'asc' ? orderA - orderB : orderB - orderA;
        }
        return 0;
      }
      if (sortBy === 'name') {
        const na = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        const nb = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
        return sortOrder === 'asc' ? na.localeCompare(nb) : nb.localeCompare(na);
      }
      if (sortBy === 'address') {
        const va = formatAddressForTable(a).toLowerCase();
        const vb = formatAddressForTable(b).toLowerCase();
        return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortBy === 'home') {
        const va = String(a.home_phone || a.phone || '').toLowerCase();
        const vb = String(b.home_phone || b.phone || '').toLowerCase();
        return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortBy === 'cell') {
        const va = String(a.cell_phone || '').toLowerCase();
        const vb = String(b.cell_phone || '').toLowerCase();
        return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortBy === 'fax') {
        const va = String(a.fax || '').toLowerCase();
        const vb = String(b.fax || '').toLowerCase();
        return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      if (sortBy === 'email') {
        const va = String(a.email || '').toLowerCase();
        const vb = String(b.email || '').toLowerCase();
        return sortOrder === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      const va = a[sortBy] || '';
      const vb = b[sortBy] || '';
      return sortOrder === 'asc'
        ? String(va).localeCompare(String(vb))
        : String(vb).localeCompare(String(va));
    });
    // Remove optimistic deletes
    filtered = filtered.filter(admin => !optimisticDeletedIds.includes(admin.id));
    setFilteredAdmins(filtered);
  }, [adminsSource, localSearchTerm, sortBy, sortOrder, optimisticDeletedIds, statusFilter]);

  const entriesPerPageNum = entriesPerPage === 'all' ? filteredAdmins.length : parseInt(entriesPerPage, 10);
  const totalPages = entriesPerPage === 'all' || !entriesPerPageNum ? 1 : Math.ceil(filteredAdmins.length / entriesPerPageNum) || 1;
  const startIndex = (currentPage - 1) * entriesPerPageNum;
  const currentAdmins =
    entriesPerPage === 'all' ? filteredAdmins : filteredAdmins.slice(startIndex, startIndex + entriesPerPageNum);

  const handleSort = useCallback(
    (key) => {
      if (sortBy === key) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(key);
        setSortOrder('asc');
      }
    },
    [sortBy]
  );

  const formatAddressForTable = (admin) => {
    if (!admin) return 'N/A';
    const primaryLine =
      admin.address_line_1 || admin.address?.address_line_1 || admin.address_1 || '';
    return primaryLine && String(primaryLine).trim() ? String(primaryLine).trim() : 'N/A';
  };

  const getSafeValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string' && value.trim() === '') return 'N/A';
    return value;
  };

  const getAdminDisplayName = (data) => {
    const first = data?.first_name || '';
    const last = data?.last_name || '';
    if (last && first) return `${last}, ${first}`;
    return `${first} ${last}`.trim() || 'N/A';
  };

  const formatAdminAddressLines = (data) => {
    if (!data) return ['N/A'];
    const addr = data.address || {};
    return buildDetailAddressLines({
      line1: data.address_line_1 ?? addr.address_line_1 ?? data.address_1,
      line2: data.address_line_2 ?? addr.address_line_2 ?? data.address_2,
      city: data.city ?? addr.city,
      stateDisplay: resolveStateDisplay(data),
      zip: data.zip_code ?? data.zipCode ?? addr.zip_code ?? addr.zip
    });
  };

  const handleAddAdmin = useCallback(() => {
    setAdminToEditModal(null);
    setIsViewAdminModal(false);
    setShowAddAdminModal(true);
  }, []);

  const handleViewAdmin = useCallback((admin) => {
    if (!admin) return;
    setAdminToEditModal(admin);
    setIsViewAdminModal(true);
    setShowAddAdminModal(true);
  }, []);

  const handleEditAdmin = useCallback((adminOrId) => {
    let admin = null;
    if (adminOrId && typeof adminOrId === 'object') {
      admin = adminOrId;
    } else if (adminOrId != null) {
      admin =
        currentAdmins.find((a) => String(a.id) === String(adminOrId)) ||
        adminsSource?.find((a) => String(a.id) === String(adminOrId));
    }
    if (admin) {
      setAdminToEditModal(admin);
      setIsViewAdminModal(false);
      setShowAddAdminModal(true);
    }
  }, [currentAdmins, adminsSource]);

  const handleDeleteAdmin = useCallback((admin) => {
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = async () => {
    if (!adminToDelete) return;
    const idToDelete = adminToDelete.id;
    try {
      // Optimistic UI: Remove from list and close modal immediately
      setOptimisticDeletedIds(prev => [...prev, idToDelete]);
      setShowDeleteModal(false);
      setAdminToDelete(null);
      toast.success(practiceId ? 'Practice admin deleted' : 'System admin deleted');

      if (practiceId) {
        await dispatch(deleteAdmin({ practiceId, adminId: idToDelete })).unwrap();
      } else {
        await adminService.deleteSystemAdmin(idToDelete);
        await fetchAllAdmins();
      }
    } catch (err) {
      // Rollback on error
      setOptimisticDeletedIds(prev => prev.filter(id => id !== idToDelete));
      
      console.error('Error deleting admin:', err);
      if (practiceId) {
        await dispatch(fetchAdmins({ practiceId })).catch(() => {});
      } else {
        await fetchAllAdmins();
      }
      
      const msg = err?.message || err || '';
      if (msg.includes('already') || msg.includes('not found')) {
        toast.info('Admin not found or already deleted');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Failed to delete admin');
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setAdminToDelete(null);
  };

  const columns = useMemo(
    () => {
      const useStaffLayout = Boolean(practiceId);
      return [
      staffCol({
        key: 'idx',
        title: '#',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (_, rowIndex) => <span>{startIndex + rowIndex + 1}</span>
      }, 'idx', useStaffLayout),
      staffCol({
        key: 'status',
        title: 'Status',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (row) => <StatusBadge status={normalizeAdminStatus(row.status)} />
      }, 'status', useStaffLayout),
      staffCol({
        key: 'name',
        title: 'Name',
        sortable: true,
        render: (row) => (
          <a
            href="#"
            className="admin-management-name-link"
            onClick={(e) => {
              e.preventDefault();
              handleViewAdmin(row);
            }}
          >
            {`${row.last_name || ''}, ${row.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A'}
          </a>
        )
      }, 'name', useStaffLayout),
      staffCol({ 
        key: 'address', 
        title: 'Address', 
        sortable: true, 
        render: (row) => {
          const addr = formatAddressForTable(row);
          return (
            <div 
              style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} 
              title={addr}
            >
              {addr}
            </div>
          );
        }
      }, 'address', useStaffLayout),
      staffCol({ key: 'home', title: 'Home Phone', sortable: true, render: (row) => formatPhoneForDisplay(row.home_phone || row.phone, 'N/A') }, 'homePhone', useStaffLayout),
      staffCol({ key: 'cell', title: 'Cell Phone', sortable: true, render: (row) => formatPhoneForDisplay(row.cell_phone, 'N/A') }, 'cellPhone', useStaffLayout),
      staffCol({ key: 'fax', title: 'Fax', sortable: true, render: (row) => formatPhoneForDisplay(row.fax, 'N/A') }, 'fax', useStaffLayout),
      staffCol({ key: 'email', title: 'Email', sortable: true, render: (row) => row.email || 'N/A' }, 'email', useStaffLayout),
      ...(!practiceId ? [{
        key: 'practices',
        title: 'No of Practices',
        sortable: false,
        className: 'reusable-table-centered-col',
        render: (row) => row.assigned_practice_count ?? row.practices?.length ?? 0
      }] : []),
      staffCol({
        key: 'action',
        title: 'Action',
        className: 'reusable-table-centered-col',
        render: (row) => (
          <ActionButtons
            onView={() => handleViewAdmin(row)}
            onEdit={allowEdit ? () => handleEditAdmin(row) : null}
            onDelete={allowDelete ? () => handleDeleteAdmin(row) : null}
          />
        )
      }, 'action', useStaffLayout)
    ];
    },
    [startIndex, allowEdit, allowDelete, practiceId, handleViewAdmin, handleEditAdmin, handleDeleteAdmin]
  );

  const showTableLoading =
    (practiceId && !hasPracticeCache && isLoading) || (!practiceId && (isLoading || loadingAllAdmins));

  const entriesSelectValue =
    entriesPerPage === 'all' || entriesPerPage === 9999 ? 'all' : String(entriesPerPage);

  const toolbarEl = (
    <div className={practiceId ? 'admin-management-header-actions' : 'scm-header-actions'}>
      <SearchBar
        value={localSearchTerm}
        onChange={setLocalSearchTerm}
        className={practiceId ? 'admin-management-search-wrapper' : 'scm-search-wrapper'}
        placeholder="Search admins..."
        label={null}
      />

      {!practiceId ? (
        <select
          className="scm-status-select"
          value={statusFilter === 'All' ? '' : statusFilter.toLowerCase()}
          onChange={(e) => {
            const val = e.target.value;
            setStatusFilter(val ? val.charAt(0).toUpperCase() + val.slice(1) : 'All');
            setCurrentPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      ) : (
        <div className="admin-management-filter-group">
          <select
            className="admin-management-status-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      )}

      {allowCreate && (
        <button
          type="button"
          className={practiceId ? 'admin-management-add-btn' : 'scm-add-btn'}
          onClick={handleAddAdmin}
        >
          <FontAwesomeIcon icon={faPlus} className={practiceId ? 'admin-management-add-icon' : 'scm-add-icon'} />
          {practiceId ? 'Add Practice Admin' : 'Add System Admin'}
        </button>
      )}
    </div>
  );

  const tableCardEl = (
    <div className={practiceId ? STAFF_LIST_TABLE_CARD : 'scm-table-wrap practice-tab-table-card'}>
      <Table
        columns={columns}
        data={currentAdmins}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        isLoading={showTableLoading}
        loadingMode="skeleton"
        skeletonRows={10}
        emptyMessage={practiceId ? '' : 'No admins found'}
      />

      {!showTableLoading && currentAdmins.length === 0 && (
        <div className="admin-management-empty-below-table">
          <FontAwesomeIcon icon={faUser} className="admin-management-empty-icon" />
          <p>No admins found</p>
          {allowCreate && (
            <button type="button" className="admin-management-btn-primary" onClick={handleAddAdmin}>
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              {practiceId ? 'Add Your First Practice Admin' : 'Add Your First System Admin'}
            </button>
          )}
        </div>
      )}

      {practiceId ? (
        <ListScreenPaginationBar
          entriesValue={entriesPerPage}
          onEntriesChange={(val) => {
            setEntriesPerPage(val);
            setCurrentPage(1);
          }}
          currentPage={currentPage}
          totalRecords={filteredAdmins.length}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          leftSlot={!hideEventLogsButton && (
            <button
              type="button"
              className="admin-management-events-log-btn"
              onClick={() => {
                if (typeof onOpenEventLogs === 'function') {
                  onOpenEventLogs();
                  return;
                }
                navigate(
                  appendFromDashboardQuery(`/event-logs/Admin/all?practiceId=${practiceId}`, location)
                );
              }}
            >
              <FontAwesomeIcon icon={faList} className="admin-management-add-icon" />
              Event Logs
            </button>
          )}
        />
      ) : (
        <div className="scm-list-footer">
          <ListScreenPaginationBar
            entriesValue={entriesSelectValue}
            onEntriesChange={(val) => {
              setEntriesPerPage(val);
              setCurrentPage(1);
            }}
            currentPage={currentPage}
            totalRecords={filteredAdmins.length}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
    </div>
  );

  const modalsEl = (
    <>
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button type="button" className="modal-close" onClick={cancelDelete}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the admin{' '}
                <strong>
                  {adminToDelete?.first_name} {adminToDelete?.last_name}
                </strong>
                ?
              </p>
              <p className="modal-warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={cancelDelete}>
                Cancel
              </button>
              <button type="button" className="btn-delete" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddAdminModal && (
        <AddAdminModal
          show={showAddAdminModal}
          practiceId={practiceId}
          isViewMode={isViewAdminModal}
          isEditMode={!!adminToEdit && !isViewAdminModal}
          editId={adminToEdit?.id}
          onHide={() => {
            const wasView = isViewAdminModal;
            setShowAddAdminModal(false);
            setAdminToEditModal(null);
            setIsViewAdminModal(false);
            if (!wasView) {
              if (practiceId) {
                dispatch(fetchAdmins({ practiceId }));
              } else {
                fetchAllAdmins();
              }
            }
          }}
        />
      )}
    </>
  );

  if (!practiceId) {
    return (
      <div className="list-screen-page-container scm-page">
        <div className="scm-page-header">
          <h2 className="dashboard-title">System Admin List</h2>
        </div>

        {error && !showTableLoading && (
          <div className="alert alert-danger mb-3" role="alert">
            {formatDisplayError(error)}
          </div>
        )}

        <div className="scm-list-frame">
          <div className="scm-list-panel">
            <div className="scm-list-body">
              <div className="scm-header-row">{toolbarEl}</div>
              {tableCardEl}
            </div>
          </div>
        </div>

        <div className="admin-management-event-log-actions">
          <button
            type="button"
            className="scm-events-log-btn"
            onClick={() => setShowLogSection(true)}
          >
            <FontAwesomeIcon icon={faList} className="list-screen-me-2" />
            Event Logs
          </button>
        </div>

        {showLogSection && (
          <div ref={logOverlayRef} className="practices-list-modal-overlay">
            <div
              ref={logContainerRef}
              className="practices-list-modal-container"
              style={{ maxWidth: '900px', width: '90%' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="practices-list-modal-header">
                <h5 className="practices-list-modal-title">Admin Event Logs</h5>
                <button
                  type="button"
                  className="practices-list-modal-close"
                  onClick={() => setShowLogSection(false)}
                >
                  ×
                </button>
              </div>
              <div className="practices-list-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <EventLogSection moduleId="Admin" targetUserId="all" />
              </div>
            </div>
          </div>
        )}

        {modalsEl}
      </div>
    );
  }

  return (
    <div className="admin-management-container">
      {error && !showTableLoading && (
        <div className="admin-management-alert admin-management-alert-danger mb-3" role="alert">
          {formatDisplayError(error)}
        </div>
      )}
      <div className="admin-management-header-row">
        {toolbarEl}
      </div>

      {tableCardEl}

      {modalsEl}
    </div>
  );
};

export default AdminManagement;
